import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useLeasingUrlParams } from '../hooks/useLeasingUrlParams';

import { Accordion, Box, Card } from '@zillow/constellation-10';
import { LoadingButton, LoadingHeading, LoadingText } from '@zg-rentals/loading-components';
import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';
import { useQueryClient } from '@zg-rentals/react-query5';

import SectionWrapper from '../components/SectionWrapper';
import { SectionProvider } from '../contexts/SectionContext';
import SectionCategoryRepresentation from './LeaseSummary/SectionCategoryRepresentation';
import type { SectionCategory } from '../models/Summary';
import Summary from '../models/Summary';
import type { Section } from '../models';
import { Person } from '../models';
import UserInfoVerifier from '../components/UserInfoVerifier';
import {
  useLeaseFromOuidQuery,
  useLeaseQuery,
  useLeaseSummaryFromOuidQuery,
  useLeaseSummaryQuery,
  useLeasingStatusForNodeQuery,
  useReevaluateLeaseCompletionMutation,
  useSubmitMiniWorkflowDataMutation,
} from '../utils/queries';
import { logError } from '@zg-rentals/log-error';

type Props = {
  categoryDescriptionOverrides?: { [sectionCategoryId: string]: React.ReactNode };
  isAlwaysExpanded?: boolean;
  summary?: Summary;
  supportEdit?: boolean;
  cta?: React.ReactNode;
  isColandlord?: boolean;
  isSelfServe?: boolean;
};

export function LeaseSummary({
  summary: summaryProp,
  isAlwaysExpanded,
  cta,
  categoryDescriptionOverrides,
  supportEdit,
  isColandlord = false,
  isSelfServe = false,
}: Props): JSX.Element {
  const { leaseId } = useLeasingUrlParams();
  const queryClient = useQueryClient();

  const [editingNodeId, setEditingNodeId] = useState<number | null>(null);

  const sectionForNodeQuery = useLeasingStatusForNodeQuery(editingNodeId || 0, leaseId, { enabled: !!editingNodeId });

  const section = sectionForNodeQuery.data;

  const ouid = isColandlord ? Person.getOUIDfromSearch(window.location.search) : '';

  const landlordLeaseQuery = useLeaseQuery(leaseId, { enabled: !!leaseId && !isColandlord });
  const colandlordLeaseQuery = useLeaseFromOuidQuery(ouid, !!isColandlord);
  const lease = isColandlord ? colandlordLeaseQuery.data : landlordLeaseQuery.data;

  const leaseSummaryQuery = useLeaseSummaryQuery(leaseId, LeaseArtifactType.LEASE, { enabled: !isColandlord });
  const leaseSummaryFromOuidQuery = useLeaseSummaryFromOuidQuery(ouid, !!isColandlord);

  const summaryData = isColandlord ? leaseSummaryFromOuidQuery.data : leaseSummaryQuery.data;
  const summary = useMemo(
    () => summaryProp || (summaryData ? new Summary({ sectionCategories: summaryData.summary }) : null),
    [summaryProp, summaryData],
  );

  const reevaluateLeaseCompletionMutation = useReevaluateLeaseCompletionMutation();
  const submitMiniWorkflowDataMutation = useSubmitMiniWorkflowDataMutation();

  const getLeasingStatus = useCallback((nodeId: number): void => {
    setEditingNodeId(nodeId);
  }, []);

  const onSectionModalClose = useCallback(
    (shouldInvalidate = false): void => {
      setEditingNodeId(null);
      if (shouldInvalidate) {
        queryClient.invalidateQueries({
          predicate: (query) => query.meta?.tags?.includes('leasing:lease-status-change'),
        });
      }
    },
    [queryClient],
  );

  const submitMiniWorkflowDataWithInvalidation = useCallback(
    async (sectionToSubmit: Section): Promise<void> => {
      const data = sectionToSubmit.getSubmitJSON();

      const response = await submitMiniWorkflowDataMutation.mutateAsync({
        postData: { ...data, leaseId: leaseId as unknown as number },
      });

      const { nextNodeId } = response;

      if (nextNodeId) {
        setEditingNodeId(nextNodeId);
      } else {
        setEditingNodeId(null);
      }

      try {
        await reevaluateLeaseCompletionMutation.mutateAsync({
          postData: {
            leaseArtifactType: LeaseArtifactType.LEASE,
            leaseArtifactId: leaseId as unknown as number,
          },
        });
      } catch (error) {
        logError({ error, context: { details: 'Failed to reevaluate lease completion', leaseId } });
      }
    },
    [leaseId, reevaluateLeaseCompletionMutation, queryClient, submitMiniWorkflowDataMutation],
  );

  function determineExpandedIncompleteSections(): Array<string> {
    const incompleteSectionsArray = [];
    summary?.sectionCategories.map((category) => {
      if (!category.isComplete()) {
        incompleteSectionsArray.push(category.displayName);
      }
    });
    return incompleteSectionsArray;
  }

  function renderSummary(): React.ReactNode {
    if (!summary) {
      if (isSelfServe) {
        return (
          <>
            <Card tone="neutral" elevated={false} css={{ marginBottom: 'tight' }}>
              <LoadingHeading
                isLoading
                level={3}
                loadingWidth="40%"
                css={{ marginBottom: 'tight', textStyle: 'responsive.heading-xs' }}
              >
                Loading
              </LoadingHeading>
              <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'tighter' }}>
                <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 4' } }}>
                  <LoadingText isLoading loadingWidth="50%">
                    Loading
                  </LoadingText>
                </Box>
                <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 8' } }}>
                  <LoadingText isLoading loadingWidth="60%">
                    Loading
                  </LoadingText>
                  <LoadingText isLoading loadingWidth="70%">
                    Loading
                  </LoadingText>
                </Box>
              </Box>
            </Card>
            <Card tone="neutral" elevated={false} css={{ marginBottom: 'tight' }}>
              <LoadingHeading
                isLoading
                level={3}
                loadingWidth="35%"
                css={{ marginBottom: 'tight', textStyle: 'responsive.heading-xs' }}
              >
                Loading
              </LoadingHeading>
              <Box
                css={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(12, 1fr)',
                  gap: 'tighter',
                  marginBottom: 'tighter',
                }}
              >
                <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 4' } }}>
                  <LoadingText isLoading loadingWidth="40%">
                    Loading
                  </LoadingText>
                </Box>
                <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 8' } }}>
                  <LoadingText isLoading loadingWidth="30%">
                    Loading
                  </LoadingText>
                  <LoadingText isLoading loadingWidth="80%">
                    Loading
                  </LoadingText>
                </Box>
              </Box>
              <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'tighter' }}>
                <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 4' } }}>
                  <LoadingText isLoading loadingWidth="45%">
                    Loading
                  </LoadingText>
                </Box>
                <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 8' } }}>
                  <LoadingText isLoading loadingWidth="30%">
                    Loading
                  </LoadingText>
                  <LoadingText isLoading loadingWidth="70%">
                    Loading
                  </LoadingText>
                  <LoadingText isLoading loadingWidth="50%" css={{ marginTop: 'tight' }}>
                    Loading
                  </LoadingText>
                  <LoadingText isLoading loadingWidth="30%">
                    Loading
                  </LoadingText>
                  <LoadingText isLoading loadingWidth="75%">
                    Loading
                  </LoadingText>
                </Box>
              </Box>
            </Card>
            <Card tone="neutral" elevated={false} css={{ marginBottom: 'tight' }}>
              <LoadingHeading
                isLoading
                level={3}
                loadingWidth="45%"
                css={{ marginBottom: 'tight', textStyle: 'responsive.heading-xs' }}
              >
                Loading
              </LoadingHeading>
              <Box
                css={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(12, 1fr)',
                  gap: 'tighter',
                  marginBottom: 'tighter',
                }}
              >
                <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 4' } }}>
                  <LoadingText isLoading loadingWidth="50%">
                    Loading
                  </LoadingText>
                </Box>
                <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 8' } }}>
                  <LoadingText isLoading loadingWidth="25%">
                    Loading
                  </LoadingText>
                </Box>
              </Box>
              <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'tighter' }}>
                <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 4' } }}>
                  <LoadingText isLoading loadingWidth="60%">
                    Loading
                  </LoadingText>
                </Box>
                <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 8' } }}>
                  <LoadingText isLoading loadingWidth="25%">
                    Loading
                  </LoadingText>
                </Box>
              </Box>
            </Card>
          </>
        );
      }

      return (
        <>
          <LoadingButton isLoading loadingWidth="25%" css={{ marginBottom: 'tight' }}>
            Loading
          </LoadingButton>
          {[1, 2, 3, 4, 5].map((index) => (
            <Card key={index} tone="neutral" elevated={false} css={{ marginBottom: 'tighter', margin: 'tighter' }}>
              <LoadingHeading
                isLoading
                level={3}
                loadingWidth="40%"
                css={{ marginBottom: 'loose', textStyle: 'responsive.heading-xs' }}
              >
                Loading
              </LoadingHeading>
              {[1, 2, 3].map((rowIndex) => (
                <Box key={rowIndex} css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'tighter' }}>
                  <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 4' } }}>
                    <LoadingText isLoading loadingWidth="50%">
                      Loading
                    </LoadingText>
                  </Box>
                  <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 7' } }}>
                    <LoadingText isLoading loadingWidth="70%">
                      Loading
                    </LoadingText>
                    <LoadingText isLoading loadingWidth="40%" css={{ marginBottom: 'tighter' }}>
                      Loading
                    </LoadingText>
                    <LoadingText isLoading loadingWidth="60%">
                      Loading
                    </LoadingText>
                  </Box>
                  <Box
                    css={{
                      gridColumnEnd: { base: 'span 12', md: 'span 1' },
                      justifySelf: { base: 'start', md: 'end' },
                    }}
                  >
                    <LoadingText isLoading loadingWidth="100%">
                      Edit
                    </LoadingText>
                  </Box>
                </Box>
              ))}
            </Card>
          ))}
        </>
      );
    }

    return (
      <>
        {cta ? <Box css={{ marginBottom: 'tight' }}>{cta}</Box> : null}
        <Accordion.Root
          type="multiple"
          defaultExpanded={
            isAlwaysExpanded
              ? summary?.sectionCategories.map((category) => category.displayName) || []
              : determineExpandedIncompleteSections()
          }
        >
          {summary?.sectionCategories.map((sectionCategory: SectionCategory, index: number) => (
            <SectionCategoryRepresentation
              key={index}
              descriptionOverride={
                categoryDescriptionOverrides && categoryDescriptionOverrides[sectionCategory.sectionCategory]
              }
              getLeasingStatusForNode={getLeasingStatus}
              showEditLink={supportEdit && lease && !lease.isReadOnly()}
              sectionCategory={sectionCategory}
            />
          ))}
        </Accordion.Root>
      </>
    );
  }

  function renderSectionWrapper(): React.ReactNode {
    // Always render SectionWrapper when editing - it handles loading states internally
    return (
      <SectionProvider initialSection={section || null}>
        <SectionWrapper
          onSubmitMiniWorkflow={submitMiniWorkflowDataWithInvalidation}
          renderModalView
          modalOnClose={onSectionModalClose}
          modalIsOpen={!!editingNodeId}
        />
      </SectionProvider>
    );
  }

  return (
    <div>
      <UserInfoVerifier leaseId={leaseId} isColandlord={isColandlord} />
      {editingNodeId && renderSectionWrapper()}
      {renderSummary()}
    </div>
  );
}

export default LeaseSummary;
